<script lang="ts" setup>
import { useBreadcrumbs } from '~layers/app/composables/useBreadcrumbs.ts'
import type { EsProduct } from '~/types/elastic'
import type { MagentoProduct } from '~/types/magento'

const route = useRoute()
const { public: config, app } = useRuntimeConfig()
const { $t } = useI18n()

const meta = inject('meta', ref())

const {
  attributes,
  m2Product,
  esProduct,
  parentCategories,
  pdpUsps,
  plpUsps,
  contentBlocks,
  fetchProductData,
  relatedProducts,
  // interestingProducts
} = usePDP(meta)

await fetchProductData()

const { loadLayout, tooltips } = useLayout()
const { label: deliveryPromiseLabel } = useDeliveryPromise([esProduct.value?.deliveryTime])

await loadLayout()

const usps = computed(() => plpUsps.value || pdpUsps.value)

const selectedProductPrice = ref<{ regularPrice?: number, finalPrice?: number } | null>(null)
const productPrices = computed(() => {
  return selectedProductPrice.value || {
    regularPrice: m2Product.value?.price_range?.minimum_price?.regular_price?.value,
    finalPrice: m2Product.value?.price_range?.minimum_price?.final_price?.value,
  }
})

const fromPrice = computed(() => {
  if (hasMultiplePrices.value && !selectedProductPrice.value) return productPrices.value
  if (!esProduct?.value.fromPrice) return null
  return {
    regularPrice: esProduct?.value.fromPrice,
    finalPrice: null, // esProduct?.value.fromPrice
  }
})

const hasMultiplePrices = computed(() => {
  if (m2Product.value?.__typename === 'ConfigurableProduct') {
    const prices = m2Product.value?.variants.map(({ product }: { product: MagentoProduct }) => product.price_range?.minimum_price?.final_price?.value)
    return prices.some((price: number) => price !== prices[0])
  }
  return false
})

const isOos = computed(() => {
  if (m2Product.value?.__typename === 'SimpleProduct') {
    return m2Product.value.stock_status === 'OUT_OF_STOCK'
  }
  if (m2Product.value?.__typename === 'ConfigurableProduct') {
    if (m2Product.value?.variants.every(item => item.product?.stock_status === 'OUT_OF_STOCK')) {
      return true
    }
    // const selectedItemsArray = Object.values(selectedItems.value)
    // if (selectedItemsArray.filter(item => item).length < selectedItemsArray.length) {
    //   return true
    // }
  }
  return false
})

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: esProduct?.value?.name,
  parents: [...parentCategories.value].sort((a, b) => a.level - b.level),
})

const { canonical } = useSeo()

const { pageView, viewItem } = useDataLayer()
pageView('detail', esProduct?.value?.name)
viewItem(
  esProduct?.value,
  { skus: m2Product.value?.variants?.map((variant: { product: { sku: string } }) => variant.product.sku) || [esProduct.value?.sku] },
)

const metaTitle = computed(
  () => esProduct?.value?.name + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => $t(`meta.pdp.description`, {
    name: esProduct?.value?.name,
    sku: esProduct?.value?.sku,
  }),
)

const combinedRelatedProducts = computed<EsProduct[]>(() => {
  return [
    ...new Map([
      ...(esProduct.value?.relatedProducts || []),
      ...(relatedProducts.value || []),
    ].map(item => [item.sku, item])).values(),
  ]
})

useHead({
  title: metaTitle,
  meta: [
    { name: 'robots', content: 'index, follow' },
    { name: 'description', content: metaDescription },
  ],
})

canonical(computed(() => route.path))

useSchemaOrg([
  defineProduct({
    'data-nosnippet': true,
    'name': esProduct?.value?.descriptiveName
      ? `${esProduct?.value?.name} ${esProduct?.value?.descriptiveName}`
      : esProduct?.value?.name,
    'image': `${config.xcdnUrl}OQ75_RM830,1066,255,255,255/-/catalog/product${esProduct?.value?.image}`,
    'sku': esProduct?.value?.sku,
    'brand': {
      name: esProduct.value?.brand?.label,
      logo: `${config.xcdnUrl}${esProduct.value?.brand?.swatch}`,
    },
    'offers': {
      url: `${config.baseDomain}${app.baseURL}${esProduct.value?.url}`.replace(/\/+/g, '/').replace(':/', '://'), // Safari compatible version
      priceCurrency: 'EUR',
      price: esProduct.value?.finalPrice || 0,
      itemCondition: 'https://schema.org/NewCondition',
      availability: `https://schema.org/${m2Product.value?.stock_status === 'OUT_OF_STOCK' ? 'OutOfStock' : 'InStock'}`,
    },
  }),
])

const detailsContainer = ref<HTMLElement | null>(null)
</script>

<template>
  <div class="pdp">
    <AppBreadcrumbs v-if="$route.path !== '/'" />

    <div class="pdp__container">
      <div class="pdp__layout">
        <div class="pdp__media">
          <ProductMediaGallery
            v-if="m2Product"
            :product="m2Product"
          >
            <div
              v-if="esProduct?.brand"
              class="pdp__brand-labels"
            >
              <ProductLabelBrand :product="esProduct" />
            </div>

            <div class="pdp__labels">
              <ProductLabelSale
                v-if="productPrices && !fromPrice"
                :prices="productPrices"
              />
              <ProductLabelMarketing
                :product="esProduct"
                :show-tooltip="true"
              />
            </div>
            <ProductMediaGalleryEnlargeButton
              class="pdp__media--enlarge-button"
            />
            <button
              class="pdp__view-specs-button"
              :aria-label="$t('Bekijk de specificaties')"
              @click="detailsContainer?.scrollIntoView({ block: 'center', behavior: 'smooth' })"
            >
              <Icon
                name="icon:notepad"
                class="icon"
                aria-hidden="true"
              />
              <span class="text">{{ $t('Bekijk de specificaties') }}</span>
            </button>
            <ProductToggleWishlist
              class="pdp__media--wishlist"
              :product="m2Product"
            />
          </ProductMediaGallery>
        </div>

        <div class="pdp__shopping">
          <div class="pdp__heading--wrapper">
            <h1 class="pdp__heading">
              {{ esProduct?.name }}
            </h1>
            <ProductToggleWishlist
              :product="m2Product"
            />
          </div>

          <div class="pdp__pricing">
            <div v-if="fromPrice">
              <UiPrice
                v-if="fromPrice"
                :prices="fromPrice"
                class="product-info__from-price"
              >
                <template #prefix>
                  <span>{{ $t('v.a.') }}</span>
                </template>
              </UiPrice>
            </div>
            <UiPrice
              v-else-if="productPrices"
              :prices="productPrices"
              class="product-info__price"
            />
          </div>

          <ProductStockMessage
            v-if="m2Product.stock_left"
            class="pdp__stock stock"
            :stock-left="m2Product.stock_left"
            :is-oos="isOos"
          >
            {{ deliveryPromiseLabel }}
          </ProductStockMessage>

          <ProductRelatedColorProducts
            class="pdp__colors"
            :product="esProduct"
          />

          <ProductCustomisation
            :attributes="attributes"
          />

          <ProductAddToCart
            v-if="m2Product"
            :product="m2Product"
            :product-name="esProduct?.name"
            :product-prices="productPrices"
            :product-from-price="fromPrice"
            @product-price="(payload) => { selectedProductPrice = payload }"
          />

          <div class="pdp__trust">
            <ProductUsps
              :attributes="attributes"
              :usps="usps"
              class="pdp__usps"
            />
            <ServiceSupport />
          </div>
        </div>
      </div>

      <div
        ref="detailsContainer"
        class="pdp__details-container"
      >
        <div class="pdp__description">
          <UiReadMore
            v-if="m2Product?.description?.html?.trim()"
            class="pdp__description--text"
          >
            <div v-html="m2Product.description.html" />
          </UiReadMore>
        </div>

        <div class="pdp__spec-tabs">
          <div class="pdp__spec--heading">
            {{ $t('Product specifications') }}
          </div>
          <ProductSpecButton
            v-if="tooltips.has('delivery-time')"
            :label="deliveryPromiseLabel"
            :description="tooltips.get('delivery-time').Description"
            modal-name="product-delivery-time"
            :modal-title="tooltips.get('delivery-time').Title"
            :disable-modal="false"
          >
            <StrapiBlocksText
              :nodes="tooltips.get('delivery-time').Richtext"
            />
          </ProductSpecButton>

          <!-- @todo -->
          <ProductSpecButton
            v-if="attributes?.poo_is_custom_made === 'Ja' && tooltips.has('customer-made')"
            :label="tooltips.get('custom-made').Title"
            :description="tooltips.get('custom-made').Description"
            modal-name="product-custom-info"
            :modal-title="tooltips.get('custom-made').Title"
            :disable-modal="false"
          >
            <StrapiBlocksText
              :nodes="tooltips.get('custom-made').Richtext"
            />
          </ProductSpecButton>

          <ProductSpecButton
            v-if="tooltips.has('delivery-info')"
            :label="tooltips.get('delivery-info').Title"
            :description="tooltips.get('delivery-info').Description"
            modal-name="product-delivery-info"
            :modal-title="tooltips.get('delivery-info').Title"
            :disable-modal="false"
          >
            <StrapiBlocksText
              :nodes="tooltips.get('delivery-info').Richtext"
            />
          </ProductSpecButton>

          <ProductSpecButton
            v-if="m2Product?.attributeGroups.length > 0 && tooltips.has('product-specifications')"
            :label="tooltips.get('product-specifications').Title"
            :description="tooltips.get('product-specifications').Description"
            modal-name="product-specifications"
            :modal-title="tooltips.get('product-specifications').Title"
          >
            <StrapiBlocksText
              :nodes="tooltips.get('product-specifications').Richtext"
            />
            <template
              v-for="(attributeGroup) in m2Product?.attributeGroups"
              :key="attributeGroup"
            >
              <table class="specifications__table table table--spacing table--grid-rows">
                <tbody>
                  <tr
                    v-for="spec in attributeGroup.attributes"
                    :key="spec.code"
                  >
                    <th>{{ spec.label }}</th>
                    <td>{{ spec.value }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
          </ProductSpecButton>

          <ProductSpecButton
            v-if="tooltips.has('warranty-info')"
            :label="tooltips.get('warranty-info').Title"
            :description="tooltips.get('warranty-info').Description"
            modal-name="product-warranty-info"
            :modal-title="tooltips.get('warranty-info').Title"
            :disable-modal="false"
          >
            <StrapiBlocksText
              :nodes="tooltips.get('warranty-info').Richtext"
            />
          </ProductSpecButton>

          <ProductSpecButton
            v-if="attributes.poo_documents && tooltips.has('product-downloads')"
            :label="tooltips.get('product-downloads').Title"
            :description="tooltips.get('product-downloads').Description"
            modal-name="product-downloads"
            :modal-title="tooltips.get('product-downloads').Title"
          >
            <StrapiBlocksText
              :nodes="tooltips.get('product-downloads').Richtext"
            />
            <ProductDownloads
              class="pdp__downloads"
              :data="attributes.poo_documents"
            />
          </ProductSpecButton>
        </div>

        <div class="pdp__details-media">
          <img
            v-if="m2Product?.image?.url"
            :src="`${m2Product?.image?.url}?f=rs:fit:816:408:0:1`"
            :alt="esProduct?.name"
            width="816"
            height="408"
            loading="lazy"
            class="pdp__detail-image"
          >
        </div>
      </div>
    </div>

    <StrapiSlices
      v-if="contentBlocks?.Slices?.length"
      :slices="contentBlocks.Slices"
    />

    <ProductSlider
      v-if="combinedRelatedProducts.length"
      :products="combinedRelatedProducts"
      class="pdp__slider pdp__related"
    >
      <h6
        class="pdp__slider-title"
        v-text="$t('Related Products')"
      />
    </ProductSlider>

    <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-1" />

    <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-2" />

    <ThirdpartySqueezelyProductSlider id="spotler-activate-pdp-slider-3" />

    <!--  -->
    <!--  -->
    <!--  -->
  </div>
</template>

<style>
@import url("~layers/app/pages/~pdp.css");
</style>
